import { useEffect, useMemo } from 'react';
import { allSettled } from 'effector';
import { Provider, useUnit } from 'effector-react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  CircularProgress,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  OutlinedInputProps,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { Listbox } from '@features/effector-form/components/listbox';
import { withControl } from '@features/effector-form/hoc/withControl';
import {
  CustomControlProps,
  GeneralControlProps,
} from '@features/effector-form/types/control';
import { ErrorMessage } from './components/error';
import { $isLoading } from './models/loader';
import {
  $options,
  ControlGate,
  createScope,
  setOptions,
  startRequest,
} from './models/main';
import * as Styled from './styled';
import { fastFilters, FastFilters } from './types/fast.filters';
import { Option } from './types/main';

type AdditionalProps = {
  id: string; // Уникальный id
  fast_filters: FastFilters; // Быстрые фильтры для запроса
  parent_guids?: string | string[] | null; // parents для запроса только в нужном объекте
  clearIcon?: boolean; // Нужна ли иконка очистки поля (если не указать, то иконка будет)
  multiple?: boolean; // Множественный выбор
  prependOptions?: Option[]; // Добавить опции в начало
  appendOptions?: Option[]; // Добавить опции в конец
};

/** Получение complex, buildings и тд. на V4 */

export const ObjectsV4ControlBody = ({
  id,
  label,
  value,
  setValue,
  error,
  required,
  readOnly,
  disabled,
  clearIcon,
  multiple,
  fast_filters,
  placeholder,
  parent_guids,
  prependOptions = [],
  appendOptions = [],
}: GeneralControlProps & CustomControlProps<OutlinedInputProps & AdditionalProps>) => {
  const [options, isLoading] = useUnit([$options, $isLoading]);
  const scope = createScope(id);
  const valueByOptions = getValueByOptions();
  const noValue = value === null || value === undefined || (multiple && !value.length);
  const emptyValue = multiple ? [] : null;
  const noParentGuids =
    !parent_guids || (Array.isArray(parent_guids) && !parent_guids.length);
  const memoParentGuids = useMemo(
    () => (Array.isArray(parent_guids) ? parent_guids.join(',') : parent_guids),
    [parent_guids]
  );

  const setEmptyValue = () => {
    if (!noValue) setValue(emptyValue);
  };

  const setEmptyOptions = async () => {
    if (!options?.length) return;

    await allSettled(setOptions, { scope, params: [] });
  };

  const handleChange = (_: any, newValue: any) => {
    if (multiple) {
      setValue(newValue.map(({ id }: Option) => id) || null);
    } else {
      setValue(newValue?.id || null);
    }
  };

  function getValueByOptions() {
    if (value === null) return value;

    if (!options && multiple) return [];
    if (!options && !multiple) return null;

    return multiple
      ? options?.filter((item) => value?.indexOf(item.id) > -1)
      : options?.find((item) => item.id === value) ?? null;
  }

  const resolveEndAdornment = (params: AutocompleteRenderInputParams) => {
    if (readOnly) return null;

    if (isLoading) return <CircularProgress size={20} color="inherit" />;

    return params.InputProps.endAdornment;
  };

  const resolveStartAdornment = (params: AutocompleteRenderInputParams) => {
    if (!readOnly) return params.InputProps.startAdornment;

    if (isLoading) return <CircularProgress size={20} color="inherit" />;

    return params.InputProps.startAdornment;
  };

  const setValueAfterOptionsChange = () => {
    const checkedValue = getValueByOptions();

    if (!checkedValue) {
      setEmptyValue();

      return;
    }
    setValue(
      Array.isArray(checkedValue) ? checkedValue.map(({ id }) => id) : checkedValue.id
    );
  };

  const getParentGuidsForRequest = () => {
    if (noParentGuids) return undefined;

    return Array.isArray(parent_guids) ? parent_guids : [parent_guids];
  };

  const handleStartRequest = async () => {
    await allSettled(startRequest, {
      scope,
      params: {
        fast_filters: {
          [fast_filters]: 1,
        },
        parent_guids: getParentGuidsForRequest(),
        id,
      },
    });
  };

  const getOptionsWithPrependedAndAppended = () => {
    if (options === null) return options;
    if (fast_filters !== fastFilters.is_complex && noParentGuids) return options;

    return [...prependOptions, ...options, ...appendOptions];
  };

  const initControl = () => {
    if (fast_filters !== fastFilters.is_complex && noParentGuids) {
      setEmptyValue();
      setEmptyOptions();

      return;
    }

    if (fast_filters === fastFilters.is_complex && options?.length) return;

    handleStartRequest();
  };

  useEffect(() => {
    if (options === null) return;

    setValueAfterOptionsChange();
  }, [options]);

  useEffect(() => {
    initControl();
  }, [fast_filters, memoParentGuids]);

  return (
    <>
      <ControlGate />

      <ErrorMessage id={id} />

      <Styled.CustomFormControl>
        {label && <FormLabel required={!readOnly && required}>{label}</FormLabel>}

        {readOnly && noValue && options !== null ? (
          <OutlinedInput
            value={valueByOptions?.label ?? '-'}
            readOnly={readOnly}
            disabled={disabled}
          />
        ) : (
          <Autocomplete
            loading={isLoading}
            value={valueByOptions}
            options={getOptionsWithPrependedAndAppended() ?? []}
            onChange={handleChange}
            ListboxComponent={Listbox}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                InputProps={{
                  ...params.InputProps,
                  required,
                  readOnly,
                  disabled: isLoading || disabled,
                  endAdornment: resolveEndAdornment(params),
                  startAdornment: resolveStartAdornment(params),
                }}
              />
            )}
            readOnly={readOnly}
            disabled={isLoading || disabled}
            multiple={multiple}
            clearIcon={clearIcon}
            size="small"
          />
        )}

        {error && <FormHelperText error>{error}</FormHelperText>}
      </Styled.CustomFormControl>
    </>
  );
};

export const ObjectsV4Control = withControl<OutlinedInputProps & AdditionalProps>(
  (props) => {
    const scope = createScope(props.id);

    return (
      <Provider value={scope}>
        <ObjectsV4ControlBody key={props.id} {...props} />
      </Provider>
    );
  }
);
